import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_confort-concierge/artifacts/qrkok7lw_Untitled%20design%2811%29.png";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/conciergerie', label: 'La Conciergerie' },
    { href: '/reglementation-airbnb', label: 'Réglementation' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header 
        className={`fixed top-0 w-full z-50 transition-colors duration-300 ${
          isScrolled ? 'bg-[#050505]/95 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <nav className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Confort Hôte" 
                className="h-12 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm tracking-wide transition-colors duration-300 ${
                    isActive(link.href) 
                      ? 'text-[#C5A059]' 
                      : 'text-[#A3A3A3] hover:text-[#F5F5F5]'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Phone & CTA */}
            <div className="hidden md:flex items-center gap-6">
              <a 
                href="tel:0689224402" 
                className="flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF37] transition-colors duration-300"
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">06 89 22 44 02</span>
              </a>
              <Link
                to="/contact"
                className="bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
                data-testid="cta-audit-nav"
              >
                Audit Gratuit
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-[#F5F5F5]"
              aria-label="Menu"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-[#050505] z-40 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8 pt-20">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className={`text-2xl font-playfair ${
                      isActive(link.href) ? 'text-[#C5A059]' : 'text-[#F5F5F5]'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 flex flex-col items-center gap-4"
              >
                <a 
                  href="tel:0689224402" 
                  className="flex items-center gap-2 text-[#C5A059] text-xl"
                >
                  <Phone className="w-5 h-5" />
                  <span>06 89 22 44 02</span>
                </a>
                <Link
                  to="/contact"
                  className="bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold mt-4"
                >
                  Demander un Audit
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
